import React, { useState } from "react"
import { Table } from "react-bootstrap"
import { useSelector } from "react-redux"
import _ from "lodash"

import {
  canEdit,
  getField,
  getLinkableComponentOptions,
  getTimeline,
} from "@/onboarding/features/product/selectors"
import { ReservedFields } from "@/onboarding/features/product/models/Product"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import ReferencedValueDisplay from "@/onboarding/features/product/components/shared/ReferencedValueDisplay"
import { isBlank } from "@/utils"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import EditIcon from "@/onboarding/features/product/components/shared/EditIcon"
import { useModal } from "@/helpers/useModal"
import EditModal from "@/onboarding/features/product/components/shared/EditModal"

import Edit from "./itinerary/Edit"
import { sumSegmentDurations, segmentDurationWarning, DURATION_WARNING } from "./durationWarning"

const formatDuration = (duration: number) => {
  const hours = Math.floor(duration / 60)
  const minutes = duration % 60

  if (hours === 0) {
    return `${minutes}m`
  }

  if (minutes === 0) {
    return `${hours}h`
  }

  return `${hours}h${minutes}m`
}

const formatTimes = ({ startTime, endTime }): string => {
  if (isBlank(startTime) || isBlank(endTime)) {
    return ""
  }

  return `${startTime} - ${endTime}`
}

const StopRow = ({ stop, isPrevious = false, showPrevious }): JSX.Element => {
  const time = isPrevious ? stop.time : formatTimes(stop)
  const linkableComponents = useSelector(getLinkableComponentOptions)

  return (
    <tr>
      <td>
        <TextDisplay value={time} previousValue={showPrevious} />
      </td>
      <td>
        <TextDisplay value={formatDuration(stop.duration)} previousValue={showPrevious} />
      </td>
      <td>
        <TextDisplay value={stop.name} previousValue={showPrevious} />
      </td>
      <td>
        <ReferencedValueDisplay value={stop.stopType} previousValue={showPrevious} />
      </td>
      <td>
        <ReferencedValueDisplay value={stop.transferType} previousValue={showPrevious} />
      </td>
      <td>
        <TextDisplay value={stop.steps} previousValue={showPrevious} />
      </td>
      <td>
        <TextDisplay value={stop.terrain} previousValue={showPrevious} />
      </td>
      <td>
        <TextDisplay value={stop.bathroomsAvailable} previousValue={showPrevious} />
      </td>
      <td>
        <div className="d-flex flex-column gap-1">
          {stop.linkedComponentIds.map((id) => (
            <TextDisplay
              key={id}
              value={_.find(linkableComponents, ({ value }) => value === id)?.label}
              previousValue={showPrevious}
            />
          ))}
          {stop.components.map(({ id, name }) => (
            <TextDisplay key={id} value={name} previousValue={showPrevious} />
          ))}
        </div>
      </td>
      <td>
        <TextDisplay value={stop.description} previousValue={showPrevious} />
      </td>
    </tr>
  )
}

const Timeline = ({
  requiredField = false,
  incompleteField = false,
  previousStops = null,
}): JSX.Element => {
  const editable = useSelector(canEdit)
  const stops = useSelector(getTimeline)
  const tourDuration = Number(useSelector(getField(ReservedFields.TOUR_DURATION))?.value) || 0
  const [showPrevious, setShowPrevious] = useState(false)
  const onEdit = useModal((props) => <EditModal {...props} title="Itinerary" Body={Edit} />)

  // Warn (subtly) about the segment durations: either they don't sum to the
  // tour's overall duration, or the tour duration is unset. Stay silent while
  // viewing the original segments; the helper stays silent on half-filled tours.
  const segmentsDuration = sumSegmentDurations(stops)
  const durationWarning = showPrevious ? null : segmentDurationWarning(stops, tourDuration)

  const handleToggle = () => {
    if (previousStops !== null) {
      setShowPrevious(!showPrevious)
    }
  }

  const hasNoSegments =
    (_.isEmpty(stops) && !showPrevious) || (_.isEmpty(previousStops) && showPrevious)

  const textStyle = showPrevious ? "original-product-text" : ""
  const labelStyle = previousStops ? "yellow-underline" : ""

  return (
    <div>
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-end gap-1">
          <h3 className={`h5 mb-0 ${labelStyle}`} onClick={handleToggle}>
            Segments
          </h3>
          {previousStops !== null && showPrevious && (
            <small className="text-muted fst-italic">Showing Original Segments</small>
          )}
        </div>
        {editable && <EditIcon onEdit={onEdit} />}
      </div>
      {durationWarning && (
        <div className="alert alert-warning small py-2 px-3 mt-1 mb-0" role="alert">
          <i className="fas fa-exclamation-triangle me-1" />
          {durationWarning === DURATION_WARNING.unsetTourDuration
            ? `The tour duration is not set - the segments add up to ${formatDuration(segmentsDuration)}.`
            : `Segment durations (${formatDuration(segmentsDuration)}) don't add up to the tour duration (${formatDuration(tourDuration)}).`}
        </div>
      )}
      {hasNoSegments ? (
        <p className="mb-0" style={{ color: showPrevious ? "#00448c" : "inherit" }}>
          No segments loaded
          <OperatorOnly>
            {requiredField && incompleteField && (
              <span
                data-toggle="tooltip"
                data-placement="top"
                title="This is an incomplete required component"
                className="ms-1"
                style={{ color: "red" }}
              >
                *
              </span>
            )}
          </OperatorOnly>
        </p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th className={textStyle}>Time</th>
              <th className={textStyle}>Duration</th>
              <th className={textStyle}>Name</th>
              <th className={textStyle}>Type</th>
              <th className={textStyle}>Transfer Type</th>
              <th className={textStyle}>Steps</th>
              <th className={textStyle}>Terrain</th>
              <th className={textStyle}>Bathrooms</th>
              <th className={textStyle}>Components</th>
              <th className={textStyle}>Description</th>
            </tr>
          </thead>
          <tbody>
            {(showPrevious && previousStops ? previousStops : stops).map((stop, index) => (
              <StopRow
                key={`${stop.name}-${index}`}
                stop={stop}
                isPrevious={showPrevious}
                showPrevious={showPrevious}
              />
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

export default Timeline
